import { selectAuthUiUrl } from 'util/selector/apiSelector';

import { RefreshTokenResponse } from 'api/AuthServiceApi';
import { authServiceApi } from 'container/authServiceApi';
import { updateAuthTokenCookie } from 'shared/utils/updateAuthTokenCookie';
import { AUTH_TOKEN_COOKIE } from 'shared/constants/cookies';
import posthog from 'posthog-js';
import Cookies from 'universal-cookie';

export const redirectToLogin = (ignoreReferrer?: boolean) => {
  if (posthog) {
    posthog.reset();
  }
  const referrer = ignoreReferrer ? '' : `?referrer=${window.location.href}`;
  window.location.replace(`${selectAuthUiUrl()}${referrer}`);
};

export const logout = () => authServiceApi.logout().then(() => redirectToLogin(true));

const REFRESH_LOCK_NAME = 'wb-token-refresh';

const getAuthToken = (): string | undefined => new Cookies().get(AUTH_TOKEN_COOKIE);

/** Returns the JWT's `exp` claim (seconds), or undefined if the token can't be parsed. */
export const getTokenExpiry = (token: string): number | undefined => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === 'number' ? exp : undefined;
  } catch {
    return undefined;
  }
};

/** Runs `callback` while holding a lock shared by every tab on this origin, when supported. */
const withRefreshLock = <T>(callback: () => Promise<T>): Promise<T> =>
  typeof navigator !== 'undefined' && navigator.locks
    ? navigator.locks.request(REFRESH_LOCK_NAME, callback)
    : callback();

const responseFromToken = (accessCode: string, exp: number): RefreshTokenResponse => ({
  data: {
    accessCode,
    expiresIn: Math.max(Math.floor(exp - Date.now() / 1000), 0),
    tokenType: 'Bearer',
    message: '',
  },
});

let inFlightRefresh: Promise<RefreshTokenResponse> | null = null;

/**
 * Refresh tokens are single-use (auth-service rotates them), so callers in this
 * tab share one in-flight request, and tabs take turns via a Web Lock. A tab
 * that finds the token changed while it waited reuses it instead of refreshing.
 */
export const refreshToken = () => {
  if (!inFlightRefresh) {
    const tokenBefore = getAuthToken();
    inFlightRefresh = withRefreshLock(async () => {
      const currentToken = getAuthToken();
      const currentExp =
        currentToken && currentToken !== tokenBefore ? getTokenExpiry(currentToken) : undefined;
      if (currentToken && currentExp) {
        return responseFromToken(currentToken, currentExp);
      }

      const response = await authServiceApi.refreshToken();
      updateAuthTokenCookie(response.data.accessCode, response.data.expiresIn);
      return response;
    }).finally(() => {
      inFlightRefresh = null;
    });
  }
  return inFlightRefresh;
};

const REFRESH_BEFORE_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes
const MIN_RESCHEDULE_DELAY_MS = 60 * 1000; // 1 minute

/**
 * Refreshes the token 10 minutes before `expirationDate`, then reschedules from
 * the new token's expiry. On failure, redirects to login once and stops.
 * Returns a function that cancels any pending refresh.
 */
export const scheduleTokenRefresh = ({ expirationDate }: { expirationDate: Date }) => {
  let cancelled = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const schedule = (expiresAt: number, minDelay: number) => {
    const delay = Math.max(expiresAt - Date.now() - REFRESH_BEFORE_EXPIRY_MS, minDelay);
    timeoutId = setTimeout(() => {
      // Another tab may already have refreshed: if the shared cookie now holds a
      // token that outlives the one we scheduled from, follow it instead.
      const currentToken = getAuthToken();
      const currentExp = currentToken ? getTokenExpiry(currentToken) : undefined;
      if (currentExp && currentExp * 1000 > expiresAt + MIN_RESCHEDULE_DELAY_MS) {
        schedule(currentExp * 1000, MIN_RESCHEDULE_DELAY_MS);
        return;
      }

      refreshToken().then(
        (response) => {
          if (!cancelled) {
            schedule(Date.now() + response.data.expiresIn * 1000, MIN_RESCHEDULE_DELAY_MS);
          }
        },
        () => {
          if (!cancelled) {
            redirectToLogin();
          }
        }
      );
    }, delay);
  };

  schedule(expirationDate.getTime(), 0);

  return () => {
    cancelled = true;
    clearTimeout(timeoutId);
  };
};
